// Steps/TeamStep.tsx
import React from "react";
import { Field } from "formik";
import { Form, Alert } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";

interface TeamMateOption {
  value: string;
  label: string;
  email?: string;
  isError?: boolean;
}

export const TeamStep: React.FC = () => {
  // Email validation regex
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  const handleCreateOption = (inputValue: string): TeamMateOption => {
    const trimmedEmail = inputValue.trim().toLowerCase();
    const isEmail = emailRegex.test(trimmedEmail);

    if (!isEmail) {
      return {
        value: trimmedEmail,
        label: "Invalid email format",
        isError: true,
      };
    }

    return {
      value: trimmedEmail,
      label: trimmedEmail,
      email: trimmedEmail,
    };
  };

  return (
    <div className="py-4">
      <Alert variant="info" className="mb-4">
        <Alert.Heading className="h6">
          💡 Collaborate with your team
        </Alert.Heading>
        <p className="mb-0 small">
          You can invite team members now or skip this step and invite them
          later from your account settings.
        </p>
      </Alert>

      <Form.Group className="mb-4">
        <Form.Label className="h5">Invite team members (optional)</Form.Label>
        <Field name="teammates">
          {({ field, form }) => (
            <div
              onKeyDown={(e) => {
                // Prevent form submission when Enter is pressed in the CreatableSelect
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            >
              <CreatableSelect<TeamMateOption, true>
                isMulti
                isClearable
                placeholder="Enter email addresses to invite team members..."
                value={field.value || []}
                onChange={(newValue) => {
                  // Filter out any error options before saving
                  const validOptions =
                    newValue?.filter((option) => !option.isError) || [];
                  form.setFieldValue(field.name, validOptions);
                }}
                formatCreateLabel={(inputValue) =>
                  `Invite: ${inputValue.trim().toLowerCase()}`
                }
                createOption={handleCreateOption}
                className="form-select-container"
                classNamePrefix="form-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    padding: "4px",
                    fontSize: "16px",
                  }),
                  option: (base, state) => ({
                    ...base,
                    color: state.data.isError ? "#dc3545" : base.color,
                  }),
                  multiValue: (base, state) => ({
                    ...base,
                    backgroundColor: state.data.isError
                      ? "#ffd7d7"
                      : base.backgroundColor,
                  }),
                }}
                isValidNewOption={(inputValue) => {
                  const trimmedEmail = inputValue?.trim().toLowerCase() || "";
                  return emailRegex.test(trimmedEmail);
                }}
              />
            </div>
          )}
        </Field>
        <Form.Text className="text-muted">
          Press Enter after typing each email address. Team members will receive
          an invitation to join your workspace.
        </Form.Text>
      </Form.Group>

      <div className="border border-primary bg-white p-3 rounded">
        <h6 className="mb-2 text-dark">👥 Team collaboration benefits:</h6>
        <ul className="mb-0 text-dark">
          <li>Share documents and extracted data across your team</li>
          <li>Collaborate on document processing workflows</li>
          <li>Manage permissions and access control</li>
          <li>Track team activity and usage</li>
        </ul>
      </div>
    </div>
  );
};
